// First-party, anonymous acquisition context. Never collect full URLs or identity.
const SESSION_KEY = "pandai.analytics.session.v1";
const VISITOR_KEY = "pandai.analytics.visitor.v1";
const SESSION_MS = 30 * 60 * 1000;
type Acquisition = { entryDomain: string; utmSource: string; utmMedium: string; utmCampaign: string; referrerHost: string };
type Context = Acquisition & { visitorId: string; sessionId: string; lastAt: number };
let fallback: Context | undefined;
function id() { return crypto.randomUUID(); }
const campaign = (value: string | null) => value && /^[a-zA-Z0-9_.-]{1,80}$/.test(value) ? value.toLowerCase() : "";
export function acquisition(href: string, referrer: string): Acquisition {
  const url = new URL(href);
  let referrerHost = "";
  try { referrerHost = new URL(referrer).hostname.toLowerCase(); } catch { /* no referrer */ }
  const entry = url.searchParams.get("_panda_entry");
  const entryDomain = entry === "panda1818.com" ? entry : url.hostname.replace(/^www\./, "");
  return { entryDomain, utmSource: campaign(url.searchParams.get("utm_source")),
    utmMedium: campaign(url.searchParams.get("utm_medium")), utmCampaign: campaign(url.searchParams.get("utm_campaign")), referrerHost };
}
export function normalizePath(raw: string): string {
  const path = raw.split(/[?#]/)[0];
  if (/^\/(?:me\/)?orders\//.test(path)) return "/orders/:order";
  if (/^\/products\//.test(path)) return "/products/:product";
  return ["/", "/subscribe", "/products", "/checkout", "/login", "/register", "/payment/success",
    "/transaction-record", "/profile", "/privacy-policy", "/terms-of-service"].includes(path) ? path : "/other";
}
export function sessionContext(href = location.href, referrer = document.referrer, now = Date.now()): Context {
  const source = acquisition(href, referrer);
  let stored: Context | undefined;
  let visitorId = "";
  try {
    stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null") || undefined;
    const visitor = JSON.parse(localStorage.getItem(VISITOR_KEY) || "null");
    if (visitor && visitor.expiresAt > now && /^[a-zA-Z0-9-]{20,80}$/.test(visitor.id)) visitorId = visitor.id;
  } catch { stored = fallback; }
  visitorId ||= stored?.visitorId || id();
  const paymentReturn = /^\/(payment|pay)(\/|$)/.test(new URL(href).pathname);
  // A new tagged external entry starts a session; payment redirects never reset it.
  const tagged = new URL(href).searchParams.has("_panda_entry") || !!source.utmSource;
  const changedCampaign = stored && tagged && !paymentReturn &&
    ["entryDomain", "utmSource", "utmMedium", "utmCampaign"].some(key => stored![key as keyof Acquisition] !== source[key as keyof Acquisition]);
  const ctx: Context = stored && Number.isFinite(stored.lastAt) && now - stored.lastAt < SESSION_MS && !changedCampaign
    ? { ...stored, lastAt: now }
    : { ...source, visitorId, sessionId: id(), lastAt: now };
  fallback = ctx;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(ctx));
    localStorage.setItem(VISITOR_KEY, JSON.stringify({ id: visitorId, expiresAt: now + 30 * 86400000 }));
  } catch { /* Storage restrictions must never prevent browsing or checkout. */ }
  return ctx;
}
export function analyticsContext() {
  try {
    const { lastAt: _lastAt, ...context } = sessionContext();
    return { ...context, path: normalizePath(location.pathname) };
  } catch { return undefined; }
}
export async function trackEvent(event: "page_view" | "product_view" | "checkout_view" | "payment_started", eventId = id()): Promise<void> {
  try {
    const context = analyticsContext();
    if (!context) return;
    await fetch("/api/analytics/visit", { method: "POST", credentials: "same-origin", keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...context, event, eventId }), signal: AbortSignal.timeout(5000) });
  } catch { /* Analytics is deliberately outside the checkout success path. */ }
}
let lastTracked = "";
let lastTrackedAt = 0;
export function trackPage(raw: string) {
  const now = Date.now();
  if (lastTracked === raw && now - lastTrackedAt < 1000) return; // StrictMode effect replay.
  lastTracked = raw; lastTrackedAt = now;
  const path = normalizePath(raw);
  if (["/payment/success", "/orders/:order", "/transaction-record", "/profile"].includes(path)) return;
  void trackEvent("page_view");
  if (path === "/products/:product" || path === "/subscribe" || path === "/products") void trackEvent("product_view");
  if (path === "/checkout") void trackEvent("checkout_view");
}
